<script setup lang="ts">
import { inject, ref } from "vue";
import SayErrorResponse from '@/mixins/sayErrorResponse';
import { useI18n } from "vue-i18n";
import { deleteCheckoutPeriod } from '@/api/checkoutPeriods';
import { useAuth } from '@/utils/auth'

const {t} = useI18n()
const auth = useAuth()

const emit = defineEmits(['deleted'])

const jobId = inject('jobId', 0)

const props = defineProps({
  checkoutPeriod: {
    type: Object,
    required: true
  }
})

const isDeleteCheckoutPeriodDialogVisible = ref(false)
const deleteCheckoutPeriodLoading = ref(false)
const deleteCheckoutPeriodAction = async () => {
  if(!props.checkoutPeriod || deleteCheckoutPeriodLoading.value) {
    return
  }
  deleteCheckoutPeriodLoading.value = true
  try {
    await deleteCheckoutPeriod(jobId.value, props.checkoutPeriod.id)
    emit('deleted')
    closeDeleteCheckoutPeriodDialog()
  } catch (error) {
    SayErrorResponse(error)
  } finally {
    deleteCheckoutPeriodLoading.value = false
  }
}
const openDeleteCheckoutPeriodDialog = () => {
  isDeleteCheckoutPeriodDialogVisible.value = true
}
const closeDeleteCheckoutPeriodDialog = () => {
  isDeleteCheckoutPeriodDialogVisible.value = false
}

const formatDate = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
}
</script>

<template>

  <span v-if="auth.check('delete job financial documents')" @click="openDeleteCheckoutPeriodDialog()" class="btn btn-red ml-2 mr-0 float-right">{{ t('delete') }}</span>
                
  <v-dialog
    v-model="isDeleteCheckoutPeriodDialogVisible" 
    width="auto"
    :max-width="600"
    location="center"
    >
    <v-card
      max-width="600"
      prepend-icon="mdi-delete"
      :title="$t('removeCheckoutPeriod')"
    >
      <template v-slot:text>
        <v-form
          ref="form"
          class="overflow-auto"
        >
          <p class="mb-5">
            {{ t('period') }}:<br>
            <b>
              {{ formatDate(checkoutPeriod.start_date) }}
              {{ t('upToAndIncludingAbbreviation') }}
              {{ formatDate(checkoutPeriod.end_date) }}
            </b>
          </p>
          <p>
            {{ t('areYouSureYouWantToRemoveTheCheckoutPeriod') }}
          </p>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-btn
          :text="t('delete')"
          @click="deleteCheckoutPeriodAction"
          type="submit"
          :loading="deleteCheckoutPeriodLoading"
        />
        <v-btn
          :text="t('cancel')"
          @click="closeDeleteCheckoutPeriodDialog"
          :disabled="deleteCheckoutPeriodLoading"
        />
      </template>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.btn {
  height: auto;
  margin-right: 16px;
  cursor: pointer;
  color: rgb(245, 120, 40);
  font-weight: 500;
  text-decoration: underline;
  user-select: none;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.0892857143em;
}
.btn-red {
  color: rgb(255, 0, 0);
}
.float-right {
  float: right;
}
</style>
